import type { Dispatch, SetStateAction } from 'react';
import Card from '@components/UI/Card';
import Button from '@components/UI/Button';
import { DeleteButton, EditButton, EmptyState } from '@components/UI';
import { showSpinner, hideSpinner } from '@components/Layout/LoadingSpinner';
import type { BacktestReplenishmentRule, TradingProfile } from '@einhaus/project314-types';
import type { NewBalanceReplenishmentRuleState, SaveTradingProfileSettings } from './types';

interface BalanceReplenishmentRulesSectionProps {
    backtestReplenishmentRules: BacktestReplenishmentRule[];
    setBacktestReplenishmentRules: Dispatch<SetStateAction<BacktestReplenishmentRule[]>>;
    formData: TradingProfile;
    setFormData: Dispatch<SetStateAction<TradingProfile>>;
    tradingProfileId: number;
    saveBalanceReplenishmentRule: SaveTradingProfileSettings;
    setEditingBalanceReplenishmentRuleId: Dispatch<SetStateAction<string | null>>;
    setNewBalanceReplenishmentRule: Dispatch<SetStateAction<NewBalanceReplenishmentRuleState>>;
    setShowBalanceReplenishmentRulesModal: Dispatch<SetStateAction<boolean>>;
}

const BalanceReplenishmentRulesSection = ({
    backtestReplenishmentRules,
    setBacktestReplenishmentRules,
    formData,
    setFormData,
    tradingProfileId,
    saveBalanceReplenishmentRule,
    setEditingBalanceReplenishmentRuleId,
    setNewBalanceReplenishmentRule,
    setShowBalanceReplenishmentRulesModal
}: BalanceReplenishmentRulesSectionProps) => {
    return (
        <Card className="mb-4" headerClassName="flex justify-between items-center" title="Backtester Balance Replenishment Rules">
            <Button
                variant="outline-dark"
                onClick={() => {
                    setEditingBalanceReplenishmentRuleId(null);

                    setNewBalanceReplenishmentRule({
                        balanceThresholdType: '',
                        balanceThreshold: null,
                        replenishmentType: '',
                        replenishmentAmount: null,
                        maxReplenishments: null
                    });

                    setShowBalanceReplenishmentRulesModal(true);
                }}
            >
                + Add
            </Button>
            {backtestReplenishmentRules.length === 0 ? (
                <EmptyState size="sm" title="No balance replenishment rules added yet." />
            ) : (
                <div className="overflow-x-auto">
                    <table className="table-auto w-full text-white border-collapse">
                        <thead>
                            <tr>
                                <th className="px-4 py-2 text-left">Threshold Type</th>
                                <th className="px-4 py-2 text-left">Threshold</th>
                                <th className="px-4 py-2 text-left">Replenishment Type</th>
                                <th className="px-4 py-2 text-left">Amount</th>
                                <th className="px-4 py-2 text-left">Max Replenishments</th>
                                <th className="px-4 py-2 text-left">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {backtestReplenishmentRules.map((rule, index) => (
                                <tr
                                    className={`border-b border-zinc-700 hover:bg-zinc-700 ${
                                        index % 2 === 0 ? 'bg-zinc-800' : 'bg-zinc-750'
                                    }`}
                                    key={rule.id}
                                >
                                    <td className="px-4 py-2">
                                        {rule.balanceThresholdType === 'percentageDecline' ? 'Percentage Decline' : 'Dollar Amount Decline'}
                                    </td>
                                    <td className="px-4 py-2">
                                        {rule.balanceThreshold}
                                        {rule.balanceThresholdType === 'percentageDecline' ? '%' : '$'}
                                    </td>
                                    <td className="px-4 py-2">
                                        {rule.replenishmentType === 'percentage' ? 'Percentage' : 'Dollar Amount'}
                                    </td>
                                    <td className="px-4 py-2">
                                        {rule.replenishmentAmount}
                                        {rule.replenishmentType === 'percentage' ? '%' : '$'}
                                    </td>
                                    <td className="px-4 py-2">{rule.maxReplenishments}</td>
                                    <td className="px-4 py-2">
                                        <div className="flex items-center gap-2">
                                            <EditButton
                                                size="sm"
                                                onClick={() => {
                                                    setEditingBalanceReplenishmentRuleId(rule.id);

                                                    setNewBalanceReplenishmentRule({
                                                        balanceThresholdType: rule.balanceThresholdType,
                                                        balanceThreshold: rule.balanceThreshold,
                                                        replenishmentType: rule.replenishmentType,
                                                        replenishmentAmount: rule.replenishmentAmount,
                                                        maxReplenishments: rule.maxReplenishments
                                                    });

                                                    setShowBalanceReplenishmentRulesModal(true);
                                                }}
                                            />
                                            <DeleteButton
                                                size="sm"
                                                onClick={() => {
                                                    const updatedRules = backtestReplenishmentRules.filter((r) => r.id !== rule.id);

                                                    setBacktestReplenishmentRules(updatedRules);

                                                    // Update the form data with the balance replenishment rule removed
                                                    const updatedFormData = {
                                                        ...formData,
                                                        backtestReplenishmentRules: updatedRules
                                                    };

                                                    setFormData(updatedFormData);
                                                    showSpinner();

                                                    // Use the balance replenishment rule mutation to save the updated settings
                                                    saveBalanceReplenishmentRule(
                                                        {
                                                            tradingProfileId,
                                                            settings: updatedFormData
                                                        },
                                                        {
                                                            onSuccess: () => {
                                                                hideSpinner();
                                                            },
                                                            onError: () => {
                                                                hideSpinner();
                                                            }
                                                        }
                                                    );
                                                }}
                                            />
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Card>
    );
};

export default BalanceReplenishmentRulesSection;
